import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  Skeleton,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';

import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/CloseOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SearchIcon from '@mui/icons-material/Search';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import VisibilityIcon from '@mui/icons-material/Visibility';

import Page from 'src/components/Page';
import OfferDialog from '../../components/HavcOffersComponents/OfferDialog';
import {
  ACTION_SX,
  ACTION_TONES,
  BRAND,
  C,
  DURUMLAR,
  cancelButton,
  dangerButton,
  durumBilgisi,
  durumChipSx,
  neutralButton,
  primaryButton,
  sayiyaCevir
} from '../../components/HavcOffersComponents/teklifTheme';
import {
  tldenCevir,
  tutarFormatla
} from '../../components/HavcOffersComponents/kurServisi';
import {
  getLookuplar,
  getTeklifler,
  teklifPdfIndir,
  teklifSil
} from '../../components/HavcOffersComponents/teklifApi';

// ==============================|| STILLER ||==============================

const styles = {
  root: theme => ({
    padding: theme.spacing(3),
    backgroundColor: C.canvas,
    minHeight: '100vh',
    [theme.breakpoints.down('sm')]: { padding: theme.spacing(2) }
  }),
  hero: theme => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2.5),
    flexWrap: 'wrap'
  }),
  heroIcon: {
    width: 52,
    height: 52,
    borderRadius: '16px',
    display: 'grid',
    placeItems: 'center',
    color: '#ffffff',
    flexShrink: 0,
    background: `linear-gradient(135deg, ${BRAND.main} 0%, ${BRAND.hover} 100%)`,
    boxShadow: `0 8px 20px -6px ${BRAND.main}8c`
  },
  heroTitle: {
    color: C.ink,
    fontWeight: 700,
    lineHeight: 1.2,
    letterSpacing: '-0.5px'
  },
  heroSubtitle: { color: C.inkMuted, marginTop: '2px' },
  // Ozet kartlari: hem sayac hem hizli filtre. Aktif olan turuncuya doner.
  ozetKart: aktif => ({
    padding: '14px 16px',
    borderRadius: '14px',
    cursor: 'pointer',
    userSelect: 'none',
    backgroundColor: aktif ? `${BRAND.main}14` : C.surface,
    border: `1px solid ${aktif ? BRAND.main : C.border}`,
    boxShadow: aktif ? `0 6px 16px -10px ${BRAND.main}` : '0 1px 2px rgba(16,24,40,0.04)',
    transition: 'all .16s ease',
    '&:hover': {
      borderColor: BRAND.main,
      transform: 'translateY(-1px)'
    }
  }),
  ozetEtiket: {
    fontSize: '0.7rem',
    fontWeight: 700,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    color: C.inkMuted,
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  },
  ozetDeger: { fontSize: '1.4rem', fontWeight: 800, color: C.ink, lineHeight: 1.3 },
  ozetTutar: { fontSize: '1.15rem', fontWeight: 800, color: '#9a5b00', lineHeight: 1.5 },
  ozetNokta: renk => ({
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: renk,
    flexShrink: 0
  }),
  mainCard: {
    borderRadius: '16px',
    border: `1px solid ${C.border}`,
    boxShadow: '0 1px 2px rgba(16,24,40,0.04), 0 8px 24px -12px rgba(16,24,40,0.18)',
    backgroundColor: C.surface,
    overflow: 'hidden'
  },
  filterBar: theme => ({
    padding: theme.spacing(2.5, 3),
    backgroundColor: C.headBg,
    borderBottom: `1px solid ${C.borderSoft}`,
    [theme.breakpoints.down('sm')]: { padding: theme.spacing(2) }
  }),
  field: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      backgroundColor: C.surface,
      transition: 'box-shadow .2s ease, border-color .2s ease',
      '& fieldset': { borderColor: C.border },
      '&:hover fieldset': { borderColor: `${BRAND.main}99` },
      '&.Mui-focused': { boxShadow: `0 0 0 3px ${BRAND.main}29` },
      '&.Mui-focused fieldset': { borderColor: BRAND.main }
    }
  },
  filterNeutralButton: { ...neutralButton, flex: 1 },
  filterPrimaryButton: { ...primaryButton, flex: 1 },
  tableWrap: {
    maxHeight: 'calc(100vh - 430px)',
    minHeight: 340,
    '&::-webkit-scrollbar': { width: 10, height: 10 },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#cbd5e1',
      borderRadius: 8,
      border: '3px solid transparent',
      backgroundClip: 'content-box'
    },
    '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#94a3b8' }
  },
  headCell: {
    backgroundColor: C.headBg,
    color: C.inkMuted,
    fontWeight: 700,
    fontSize: '0.75rem',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
    borderBottom: `1px solid ${C.border}`,
    padding: '12px 16px'
  },
  sortLabel: {
    '&.Mui-active': { color: C.ink },
    '&.Mui-active .MuiTableSortLabel-icon': { color: `${BRAND.main} !important` },
    '&:hover': { color: C.inkStrong }
  },
  // Sabit sutun arka planini satirdan miras aliyor; bu yuzden TEK satirlarin
  // da acik bir arka plani olmali, yoksa altindan kayan icerik gorunur.
  bodyRow: {
    backgroundColor: C.surface,
    '& .MuiTableCell-body': {
      padding: '12px 16px',
      fontSize: '0.875rem',
      color: C.inkBody,
      borderBottom: `1px solid ${C.borderSoft}`,
      whiteSpace: 'nowrap'
    },
    '&:nth-of-type(even)': { backgroundColor: C.rowAlt },
    '&:hover': { backgroundColor: `${BRAND.main}0f !important` },
    transition: 'background-color .16s ease'
  },
  // Islemler sutunu saga yapisik: yatay kaydirmada bile hep gorunur.
  headCellActions: {
    backgroundColor: C.headBg,
    color: C.inkMuted,
    fontWeight: 700,
    fontSize: '0.75rem',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
    borderBottom: `1px solid ${C.border}`,
    borderLeft: `1px solid ${C.border}`,
    padding: '12px 16px',
    position: 'sticky',
    right: 0,
    // stickyHeader basliklara z-index 2 veriyor; kose hucre onlarin da ustunde kalmali.
    zIndex: 4
  },
  cellActions: {
    position: 'sticky',
    right: 0,
    zIndex: 1,
    backgroundColor: 'inherit',
    borderLeft: `1px solid ${C.border}`,
    boxShadow: '-6px 0 8px -8px rgba(15,23,42,0.35)'
  },
  cellNo: { fontWeight: 700, color: C.ink },
  cellProje: { color: C.inkStrong, fontWeight: 500 },
  cellMusteri: { maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' },
  cellTutar: { fontWeight: 700, color: C.ink, textAlign: 'right' },
  cellTarih: { fontSize: '0.813rem', color: C.inkMuted },
  actionGroup: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '5px',
    borderRadius: '13px',
    backgroundColor: C.headBg,
    border: `1px solid ${C.borderSoft}`
  },
  actionDivider: {
    width: '1px',
    alignSelf: 'stretch',
    marginInline: '2px',
    backgroundColor: C.border
  },
  emptyState: theme => ({ padding: theme.spacing(9, 2), textAlign: 'center' }),
  footer: theme => ({
    padding: theme.spacing(1, 2),
    justifyContent: 'flex-end',
    backgroundColor: C.headBg,
    borderTop: `1px solid ${C.borderSoft}`
  }),
  skeletonCell: { padding: '12px 16px' },
  skeleton: { borderRadius: '6px' }
};

// ==============================|| SIRALAMA ||==============================

const toSortableDate = value => {
  if (!value) return 0;
  const raw = String(value).trim();
  const tr = raw.match(
    /^(\d{1,2})\.(\d{1,2})\.(\d{4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/
  );
  if (tr) {
    const [, gun, ay, yil, saat = '0', dakika = '0', saniye = '0'] = tr;
    return new Date(+yil, +ay - 1, +gun, +saat, +dakika, +saniye).getTime();
  }
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
};

const NUMERIC_COLUMNS = ['GenelToplam', 'GecerlilikGun', 'Durum'];

// Olusturma tarihi + gecerlilik gunu -> kalan gun. Tarih cozulemezse null.
const kalanGun = row => {
  const t = toSortableDate(row?.OlusturmaTarihi);
  if (!t) return null;

  const son = new Date(t);
  son.setDate(son.getDate() + (Number(row.GecerlilikGun) || 0));

  const bugun = new Date();
  bugun.setHours(0, 0, 0, 0);

  return Math.ceil((son - bugun) / 86400000);
};

const gecerlilikGorunumu = row => {
  const kalan = kalanGun(row);
  const gun = `${row.GecerlilikGun || 0} gün`;

  if (kalan === null) return { metin: gun, renk: C.inkMuted, ikon: false };
  if (kalan < 0) return { metin: 'Süresi doldu', renk: '#dc2626', ikon: true };
  if (kalan === 0) return { metin: 'Bugün son gün', renk: '#d97706', ikon: true };
  if (kalan <= 3) return { metin: `${kalan} gün kaldı`, renk: '#d97706', ikon: true };
  return { metin: `${kalan} gün kaldı`, renk: C.inkBody, ikon: false };
};

function descendingComparator(a, b, orderBy) {
  let aValue;
  let bValue;

  if (orderBy === 'OlusturmaTarihi') {
    aValue = toSortableDate(a[orderBy]);
    bValue = toSortableDate(b[orderBy]);
  } else if (NUMERIC_COLUMNS.indexOf(orderBy) !== -1) {
    aValue = sayiyaCevir(a[orderBy]);
    bValue = sayiyaCevir(b[orderBy]);
  } else {
    aValue = a[orderBy];
    bValue = b[orderBy];
  }

  if (bValue < aValue) return -1;
  if (bValue > aValue) return 1;
  return 0;
}

// orderBy bos iken servisten gelen sira korunur.
const getComparator = (order, orderBy) => {
  if (!orderBy) return () => 0;
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
};

function stableSort(array, comparator) {
  const stabilized = Array.isArray(array) ? array.map((el, index) => [el, index]) : [];
  stabilized.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilized.map(el => el[0]);
}

const headCells = [
  { id: 'TeklifNo', label: 'Teklif No', align: 'left' },
  { id: 'ProjeAdi', label: 'Proje Adı', align: 'left' },
  { id: 'MusteriAdSoyad', label: 'Müşteri', align: 'left' },
  { id: 'Bayi', label: 'Bayi', align: 'left' },
  { id: 'Servis', label: 'Servis', align: 'left' },
  { id: 'GenelToplam', label: 'Teklif Tutarı', align: 'right' },
  { id: 'Durum', label: 'Durum', align: 'left' },
  { id: 'GecerlilikGun', label: 'Geçerlilik', align: 'left' },
  { id: 'OlusturmaTarihi', label: 'Oluşturulma Tarihi', align: 'left' },
  { id: 'Actions', label: 'İşlemler', align: 'center', sortable: false }
];

// ==============================|| TABLO BASLIGI ||==============================

function EnhancedTableHead({ order, orderBy, onRequestSort }) {
  const createSortHandler = property => event => onRequestSort(event, property);

  return (
    <TableHead>
      <TableRow>
        {headCells.map(headCell => (
          <TableCell
            key={headCell.id}
            align={headCell.align}
            sortDirection={orderBy === headCell.id ? order : false}
            sx={headCell.id === 'Actions' ? styles.headCellActions : styles.headCell}
          >
            {headCell.sortable === false ? (
              headCell.label
            ) : (
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={createSortHandler(headCell.id)}
                sx={styles.sortLabel}
              >
                {headCell.label}
                {orderBy === headCell.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string
};

// ==============================|| SATIR AKSIYONU ||==============================

const RowAction = React.memo(function RowAction({
  title,
  tone,
  icon,
  disabled,
  loading,
  onClick
}) {
  const pasif = disabled || loading;

  const button = (
    <IconButton
      size="small"
      aria-label={title}
      disabled={pasif}
      onClick={onClick}
      sx={ACTION_SX[tone]}
    >
      {loading ? (
        <CircularProgress size={16} sx={{ color: ACTION_TONES[tone].fg }} />
      ) : (
        icon
      )}
    </IconButton>
  );

  return (
    <Tooltip title={loading ? 'Hazırlanıyor...' : title} arrow placement="top">
      {pasif ? <span>{button}</span> : button}
    </Tooltip>
  );
});

RowAction.propTypes = {
  title: PropTypes.string.isRequired,
  tone: PropTypes.string.isRequired,
  icon: PropTypes.node.isRequired,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  onClick: PropTypes.func.isRequired
};

// ==============================|| ISKELET ||==============================

const SkeletonRows = () =>
  Array.from({ length: 5 }).map((_, rowIndex) => (
    <TableRow key={`skeleton-${rowIndex}`}>
      {headCells.map(headCell => (
        <TableCell key={headCell.id} sx={styles.skeletonCell}>
          <Skeleton variant="rounded" height={18} sx={styles.skeleton} />
        </TableCell>
      ))}
    </TableRow>
  ));

// ==============================|| TEKLIF YONETIMI ||==============================

function HVACOffers() {
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [uyari, setUyari] = useState('');

  const [bayiler, setBayiler] = useState([]);
  const [servisler, setServisler] = useState([]);
  const [durumlar, setDurumlar] = useState([]);

  const [selectedBayi, setSelectedBayi] = useState(null);
  const [selectedDurum, setSelectedDurum] = useState(null);
  const [search, setSearch] = useState('');

  const [teklifler, setTeklifler] = useState([]);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [hizliDurum, setHizliDurum] = useState(null);

  // PDF hazirlanirken hangi satirin beklediği. 0 = islem yok.
  const [pdfBekleyen, setPdfBekleyen] = useState(0);
  const [bildirim, setBildirim] = useState('');

  const pdfIndir = row => {
    setPdfBekleyen(row.TeklifId);

    teklifPdfIndir(row.TeklifId, row.TeklifNo)
      .then(() => setBildirim(`${row.TeklifNo || 'Teklif'}.pdf indirildi`))
      .catch(() => setUyari('Teklif PDF’i oluşturulamadı. Lütfen tekrar deneyin.'))
      .finally(() => setPdfBekleyen(0));
  };

  const [selectedRow, setSelectedRow] = useState({});
  const [dialogMode, setDialogMode] = useState('create');
  const [kopyaKaynakId, setKopyaKaynakId] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [silOnayOpen, setSilOnayOpen] = useState(false);
  const [siliniyor, setSiliniyor] = useState(false);

  useEffect(() => {
    getLookuplar()
      .then(({ bayiler: b, servisler: s, durumlar: d }) => {
        setBayiler(b);
        setServisler(s);
        setDurumlar(d);
      })
      .catch(() => {
        setUyari(
          'Bayi / servis / durum listeleri yüklenemedi. Filtreler boş gelebilir; sayfayı yenileyip tekrar deneyin.'
        );
      });
  }, []);

  const sorgula = () => {
    setLoading(true);
    setHasSearched(true);
    setPage(0);

    getTeklifler({
      bayiId: selectedBayi?.Id || 0,
      durum: selectedDurum?.Id || 0,
      arama: search
    })
      .then(sonuc => {
        setTeklifler(sonuc);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        setUyari('Teklifler getirilemedi. Lütfen tekrar deneyin.');
      });
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Ozet kartindan gelen hizli filtre. Sunucuya tekrar gitmez, yuklenen
  // sonuclar uzerinde calisir.
  const hizliFiltreUygula = kod =>
    setHizliDurum(onceki => (onceki === kod ? null : kod));

  const filtrelenmis = useMemo(
    () => (hizliDurum ? teklifler.filter(t => Number(t.Durum) === hizliDurum) : teklifler),
    [teklifler, hizliDurum]
  );

  const ozet = useMemo(() => {
    const say = kod => teklifler.filter(t => Number(t.Durum) === kod).length;
    return {
      toplam: teklifler.length,
      taslak: say(DURUMLAR.TASLAK.kod),
      gonderildi: say(DURUMLAR.GONDERILDI.kod),
      onaylandi: say(DURUMLAR.ONAYLANDI.kod),
      // Toplam tutar TL bazinda; doviz teklifleri de TL karsiligiyla toplaniyor.
      tutarTL: teklifler.reduce((acc, t) => acc + (Number(t.GenelToplam) || 0), 0)
    };
  }, [teklifler]);

  const sortedRows = useMemo(
    () => stableSort(filtrelenmis, getComparator(order, orderBy)),
    [filtrelenmis, order, orderBy]
  );

  const visibleRows = useMemo(
    () => sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedRows, page, rowsPerPage]
  );

  const showEmptyState = !loading && sortedRows.length === 0;

  const yeniTeklif = () => {
    setSelectedRow({});
    setKopyaKaynakId(0);
    setDialogMode('create');
    setDialogOpen(true);
  };

  const teklifAc = (row, mode) => {
    setSelectedRow(row);
    setKopyaKaynakId(0);
    setDialogMode(mode);
    setDialogOpen(true);
  };

  // Mevcut teklifin kalemleri ve oranlariyla YENI teklif acar.
  const teklifCogalt = row => {
    setSelectedRow(row);
    setKopyaKaynakId(row.TeklifId);
    setDialogMode('create');
    setDialogOpen(true);
  };

  const kayitSonrasi = () => {
    setDialogOpen(false);
    sorgula();
  };

  const silOnayla = () => {
    setSiliniyor(true);
    teklifSil(selectedRow.TeklifId)
      .then(() => {
        setSiliniyor(false);
        setSilOnayOpen(false);
        sorgula();
      })
      .catch(() => {
        setSiliniyor(false);
        setUyari('Teklif silinemedi. Lütfen tekrar deneyin.');
      });
  };

  return (
    <Page sx={styles.root} title={'Teklif Yönetimi'}>
      <Box sx={styles.hero}>
        <Box sx={styles.heroIcon}>
          <ReceiptLongIcon />
        </Box>
        <Box>
          <Typography variant="h4" sx={styles.heroTitle}>
            Teklif Yönetimi
          </Typography>
          <Typography variant="body2" sx={styles.heroSubtitle}>
            Teklif hazırlama ve takip ekranı
          </Typography>
        </Box>
      </Box>

      {teklifler.length > 0 && (
        <Grid container spacing={2} sx={{ mb: 2.5 }}>
          {[
            { kod: null, etiket: 'Tümü', deger: ozet.toplam, renk: C.inkSoft },
            {
              kod: DURUMLAR.TASLAK.kod,
              etiket: DURUMLAR.TASLAK.etiket,
              deger: ozet.taslak,
              renk: DURUMLAR.TASLAK.fg
            },
            {
              kod: DURUMLAR.GONDERILDI.kod,
              etiket: DURUMLAR.GONDERILDI.etiket,
              deger: ozet.gonderildi,
              renk: DURUMLAR.GONDERILDI.fg
            },
            {
              kod: DURUMLAR.ONAYLANDI.kod,
              etiket: DURUMLAR.ONAYLANDI.etiket,
              deger: ozet.onaylandi,
              renk: DURUMLAR.ONAYLANDI.fg
            }
          ].map(k => (
            <Grid key={k.etiket} size={{ xs: 6, sm: 6, md: 2.4 }}>
              <Tooltip
                title={k.kod ? `${k.etiket} olanları göster` : 'Filtreyi kaldır'}
                arrow
              >
                <Box
                  sx={styles.ozetKart(hizliDurum === k.kod && k.kod !== null)}
                  onClick={() => hizliFiltreUygula(k.kod)}
                >
                  <Box sx={styles.ozetEtiket}>
                    <Box sx={styles.ozetNokta(k.renk)} />
                    {k.etiket}
                  </Box>
                  <Box sx={styles.ozetDeger}>{k.deger}</Box>
                </Box>
              </Tooltip>
            </Grid>
          ))}

          <Grid size={{ xs: 12, sm: 12, md: 2.4 }}>
            <Box sx={{ ...styles.ozetKart(false), cursor: 'default', '&:hover': {} }}>
              <Box sx={styles.ozetEtiket}>
                <Box sx={styles.ozetNokta(BRAND.main)} />
                Toplam Tutar
              </Box>
              <Box sx={styles.ozetTutar}>{tutarFormatla(ozet.tutarTL, 'TRY')}</Box>
            </Box>
          </Grid>
        </Grid>
      )}

      <Card sx={styles.mainCard} elevation={0}>
        {/* PDF uretimi sunucuda birkac saniye surebiliyor; kartin tepesinde
            ince bir surec cubugu isin devam ettigini gosteriyor. */}
        {pdfBekleyen !== 0 && (
          <LinearProgress
            sx={{
              height: 3,
              backgroundColor: `${BRAND.main}29`,
              '& .MuiLinearProgress-bar': { backgroundColor: BRAND.main }
            }}
          />
        )}
        <Box sx={styles.filterBar}>
          {uyari && (
            <Alert
              severity="warning"
              onClose={() => setUyari('')}
              sx={{ mb: 2, borderRadius: '10px' }}
            >
              {uyari}
            </Alert>
          )}

          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Autocomplete
                size="small"
                id="teklif-filter-bayi"
                fullWidth
                options={bayiler}
                getOptionLabel={o => o.Dsc || ''}
                isOptionEqualToValue={(o, v) => o.Id === v.Id}
                value={selectedBayi}
                onChange={(event, newValue) => setSelectedBayi(newValue)}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Bayi"
                    placeholder="Bayi seçiniz"
                    sx={styles.field}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Autocomplete
                size="small"
                id="teklif-filter-durum"
                fullWidth
                options={durumlar}
                getOptionLabel={o => o.Dsc || ''}
                isOptionEqualToValue={(o, v) => o.Id === v.Id}
                value={selectedDurum}
                onChange={(event, newValue) => setSelectedDurum(newValue)}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Durum"
                    placeholder="Durum seçiniz"
                    sx={styles.field}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 12, md: 4 }}>
              <TextField
                fullWidth
                id="teklif-filter-search"
                label="Arama"
                placeholder="Teklif no, proje, müşteri veya bayi ile arayın..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') sorgula();
                }}
                autoComplete="off"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: C.inkSoft, fontSize: '1.15rem' }} />
                    </InputAdornment>
                  ),
                  endAdornment: search && (
                    <InputAdornment position="end">
                      <Tooltip title="Temizle" arrow>
                        <IconButton size="small" onClick={() => setSearch('')}>
                          <CloseIcon sx={{ fontSize: '1rem' }} />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  )
                }}
                variant="outlined"
                size="small"
                sx={styles.field}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 12, md: 2 }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => sorgula()}
                  disabled={loading}
                  startIcon={
                    loading ? (
                      <CircularProgress size={16} sx={{ color: '#ffffff' }} />
                    ) : (
                      <SearchIcon />
                    )
                  }
                  sx={styles.filterNeutralButton}
                >
                  {loading ? 'Yükleniyor' : 'Sorgula'}
                </Button>
                <Tooltip title="Yeni teklif oluştur" arrow>
                  <span style={{ display: 'flex', flex: 1 }}>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={yeniTeklif}
                      startIcon={<AddIcon />}
                      sx={styles.filterPrimaryButton}
                    >
                      Yeni
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>
        </Box>

        <CardContent sx={{ padding: 0, '&:last-child': { paddingBottom: 0 } }}>
          <TableContainer sx={styles.tableWrap}>
            <Table stickyHeader aria-label="Teklif listesi">
              <EnhancedTableHead
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
              />
              <TableBody>
                {loading && <SkeletonRows />}

                {showEmptyState && (
                  <TableRow>
                    <TableCell colSpan={headCells.length} sx={{ borderBottom: 'none' }}>
                      <Box sx={styles.emptyState}>
                        {hasSearched ? (
                          <>
                            <SearchOffIcon sx={{ fontSize: 56, color: '#cbd5e1', mb: 1.5 }} />
                            <Typography variant="h6" sx={{ color: C.inkMuted, mb: 0.5 }}>
                              Sonuç Bulunamadı
                            </Typography>
                            <Typography variant="body2" sx={{ color: C.inkSoft }}>
                              Arama kriterlerinize uygun teklif bulunamadı. Lütfen farklı
                              filtreler deneyin.
                            </Typography>
                          </>
                        ) : (
                          <>
                            <TravelExploreIcon
                              sx={{ fontSize: 56, color: `${BRAND.main}73`, mb: 1.5 }}
                            />
                            <Typography variant="h6" sx={{ color: C.inkMuted, mb: 0.5 }}>
                              Sorgulamaya Hazır
                            </Typography>
                            <Typography variant="body2" sx={{ color: C.inkSoft }}>
                              Bayi ve durum seçip <strong>Sorgula</strong> düğmesine basın ya da
                              doğrudan teklif no ile arama yapın.
                            </Typography>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                )}

                {!loading &&
                  visibleRows.map((row, index) => {
                    const durum = durumBilgisi(row.Durum);

                    return (
                      <TableRow
                        hover
                        tabIndex={-1}
                        key={row.TeklifId ?? `teklif-row-${index}`}
                        sx={styles.bodyRow}
                      >
                        <TableCell sx={styles.cellNo}>{row.TeklifNo}</TableCell>
                        <TableCell sx={styles.cellProje}>{row.ProjeAdi}</TableCell>
                        <TableCell sx={styles.cellMusteri}>
                          <Tooltip title={row.MusteriTelefon || ''} arrow>
                            <span>{row.MusteriAdSoyad}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell>{row.Bayi}</TableCell>
                        <TableCell>{row.Servis}</TableCell>
                        {/* Tutar, teklifin kendi para biriminde gosteriliyor.
                            Katalog TL tuttugu icin doviz tekliflerinde cevriliyor. */}
                        <TableCell sx={styles.cellTutar} align="right">
                          <Tooltip
                            title={
                              row.ParaBirimi && row.ParaBirimi !== 'TRY'
                                ? `TL karşılığı: ${tutarFormatla(row.GenelToplam, 'TRY')} · kur ${sayiyaCevir(
                                    row.DovizKuru
                                  ).toFixed(4)}`
                                : ''
                            }
                            arrow
                          >
                            <span>
                              {row.ParaBirimi && row.ParaBirimi !== 'TRY'
                                ? tutarFormatla(
                                    tldenCevir(row.GenelToplam, row.DovizKuru),
                                    row.ParaBirimi
                                  )
                                : tutarFormatla(row.GenelToplam, 'TRY')}
                            </span>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={durum.etiket} sx={durumChipSx(row.Durum)} />
                        </TableCell>
                        <TableCell>
                          {(() => {
                            const g = gecerlilikGorunumu(row);
                            return (
                              <Tooltip
                                title={`${row.GecerlilikGun || 0} gün geçerli · ${row.OlusturmaTarihi} tarihinde oluşturuldu`}
                                arrow
                              >
                                <Box
                                  sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '5px',
                                    color: g.renk,
                                    fontWeight: g.ikon ? 700 : 400
                                  }}
                                >
                                  {g.ikon && <AccessTimeIcon sx={{ fontSize: '0.95rem' }} />}
                                  {g.metin}
                                </Box>
                              </Tooltip>
                            );
                          })()}
                        </TableCell>
                        <TableCell sx={styles.cellTarih}>{row.OlusturmaTarihi}</TableCell>
                        <TableCell align="center" sx={styles.cellActions}>
                          <Box sx={styles.actionGroup}>
                            <RowAction
                              title="Görüntüle"
                              tone="info"
                              icon={<VisibilityIcon />}
                              onClick={() => teklifAc(row, 'update')}
                            />
                            <RowAction
                              title="Teklifi Düzenle"
                              tone="edit"
                              icon={<EditIcon />}
                              onClick={() => teklifAc(row, 'update')}
                            />

                            <Box sx={styles.actionDivider} />

                            <RowAction
                              title="Kopyalayarak Yeni Teklif"
                              tone="warn"
                              icon={<ContentCopyIcon />}
                              onClick={() => teklifCogalt(row)}
                            />
                            <RowAction
                              title="PDF İndir"
                              tone="success"
                              icon={<PictureAsPdfIcon />}
                              loading={pdfBekleyen === row.TeklifId}
                              disabled={pdfBekleyen !== 0 && pdfBekleyen !== row.TeklifId}
                              onClick={() => pdfIndir(row)}
                            />

                            <Box sx={styles.actionDivider} />

                            <RowAction
                              title="Teklifi Sil"
                              tone="danger"
                              icon={<DeleteIcon />}
                              onClick={() => {
                                setSelectedRow(row);
                                setSilOnayOpen(true);
                              }}
                            />
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>

        <Divider />

        <CardActions sx={styles.footer}>
          <TablePagination
            rowsPerPageOptions={[5, 10, 20, 50, 100]}
            component="div"
            count={sortedRows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            onRowsPerPageChange={event => {
              setRowsPerPage(parseInt(event?.target.value, 10));
              setPage(0);
            }}
            labelRowsPerPage="Sayfa başına satır:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
            sx={{
              '& .MuiTablePagination-toolbar': { minHeight: 48 },
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                fontSize: '0.8125rem',
                color: C.inkMuted,
                marginBottom: 0
              }
            }}
          />
        </CardActions>
      </Card>

      <OfferDialog
        open={dialogOpen}
        mode={dialogMode}
        teklifId={selectedRow.TeklifId}
        kopyaKaynakId={kopyaKaynakId}
        bayiler={bayiler}
        servisler={servisler}
        onClose={() => setDialogOpen(false)}
        onSaved={kayitSonrasi}
      />

      <Snackbar
        open={Boolean(bildirim)}
        autoHideDuration={4000}
        onClose={() => setBildirim('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setBildirim('')}
          sx={{ borderRadius: '10px' }}
        >
          {bildirim}
        </Alert>
      </Snackbar>

      <Dialog
        open={silOnayOpen}
        onClose={() => setSilOnayOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, color: C.ink }}>Teklifi Sil</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: C.inkBody }}>
            <strong>{selectedRow.TeklifNo}</strong> numaralı teklif silinecek. Bu işlem geri
            alınamaz. Devam etmek istiyor musunuz?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setSilOnayOpen(false)}
            sx={cancelButton}
            disabled={siliniyor}
          >
            Hayır, Vazgeç
          </Button>
          <Button
            variant="contained"
            onClick={silOnayla}
            sx={dangerButton}
            disabled={siliniyor}
            startIcon={
              siliniyor ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <DeleteIcon />
            }
          >
            {siliniyor ? 'Siliniyor' : 'Evet, Sil'}
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
}

export default HVACOffers;
